import { useRef, useState } from 'react'
import { X } from 'lucide-react'
import type { Post } from '@/services/postsService'
import { RichTextEditor } from './RichTextEditor'
import { apiClient } from '@/services/apiClient'

interface PostFormModalProps {
  title: string
  post?: Post
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  userRole?: string
}

export function PostFormModal({ title, post, onClose, onSubmit, userRole }: PostFormModalProps) {
  const isCreator = userRole === 'creator'
  const [loading, setLoading] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [formData, setFormData] = useState<{
    title: string
    slug: string
    summary: string
    content: string
    status: 'draft' | 'published' | 'archived'
    cover_image_url: string
  }>({
    title: post?.title || '',
    slug: post?.slug || '',
    summary: post?.summary || '',
    content: post?.content || '',
    status: post?.status || 'draft',
    cover_image_url: post?.cover_image_url || '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const data = {
        title: formData.title,
        slug: formData.slug || undefined,
        summary: formData.summary || undefined,
        content: formData.content || undefined,
        status: formData.status,
        cover_image_url: formData.cover_image_url || undefined,
      }

      await onSubmit(data)
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao salvar post')
      setLoading(false)
    }
  }

  const handleImageUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]

    if (!file) {
      return
    }

    if (!file.type.startsWith('image/')) {
      setError('Selecione um arquivo de imagem válido')
      e.target.value = ''
      return
    }

    setError('')
    setUploadingImage(true)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const uploadResponse = await apiClient.post('/content/attachments/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const uploadedAttachment = uploadResponse.data as { file_url?: string; attachment?: { url?: string } }

      const uploadedUrl = uploadedAttachment.file_url || uploadedAttachment.attachment?.url

      if (uploadedUrl) {
        setFormData((current) => ({ ...current, cover_image_url: uploadedUrl }))
      }
    } catch (uploadError) {
      console.error('Erro ao fazer upload da imagem:', uploadError)
      setError('Não foi possível enviar a imagem. Tente novamente ou use uma URL manual.')
    } finally {
      setUploadingImage(false)
      e.target.value = ''
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden shadow-xl">
        <div className="bg-white border-b px-6 py-4 flex justify-between items-center shrink-0">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Título do post"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Slug
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="url-amigavel (deixe vazio para gerar automaticamente)"
            />
            <p className="text-xs text-gray-500 mt-1">
              Se deixar vazio, será gerado automaticamente a partir do título
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resumo
            </label>
            <textarea
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Breve descrição do post..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Conteúdo
            </label>
            <RichTextEditor
              content={formData.content}
              onChange={(html) => setFormData({ ...formData, content: html })}
              placeholder="Escreva o conteúdo do post..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Use a barra de ferramentas para formatar o texto
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL da Imagem de Capa
            </label>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="url"
                value={formData.cover_image_url}
                onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://exemplo.com/imagem.jpg"
              />
              <button
                type="button"
                onClick={handleImageUploadClick}
                disabled={uploadingImage || loading}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {uploadingImage ? 'Enviando...' : 'Upload'}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageFileChange}
            />
            <p className="text-xs text-gray-500 mt-1">
              Você pode colar uma URL externa ou enviar uma imagem local para o S3.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as typeof formData.status })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              disabled={isCreator}
              title={isCreator ? 'Apenas administradores podem alterar o status' : ''}
            >
              <option value="draft">Rascunho</option>
              {!isCreator && (
                <>
                  <option value="published">Publicado</option>
                  <option value="archived">Arquivado</option>
                </>
              )}
            </select>
            {isCreator && (
              <p className="text-xs text-gray-500 mt-1">
                Seu conteúdo será revisado por um administrador antes da publicação.
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
